// User types
export interface User {
  id?: string;
  email: string;
  name: string;
  purpose:
    | "improve_fitness"
    | "maintain_fitness"
    | "general_tracking"
    | "other"
    | "prefer_not_to_say";
  preferred_resistance_unit: "kg" | "lbs";
  preferred_time_format: "12h" | "24h";
  preferred_visibility: "private" | "friends";
}

export interface UserProfileResponse {
  email: string;
  name: string;
}

// Muscle group standardized enum (scientific terms)
export const MUSCLE_GROUPS = [
  "pectorals",
  "back",
  "deltoids",
  "biceps",
  "triceps",
  "forearms",
  "abdominals",
  "obliques",
  "quadriceps",
  "hamstrings",
  "glutes",
  "calves",
  "trapezius",
  "latissimus_dorsi",
  "adductors",
] as const;

export type MuscleGroup = (typeof MUSCLE_GROUPS)[number];

export interface MuscleGroupWeight {
  muscle_group: MuscleGroup;
  weight: number;
}

// Display names for muscle groups
export const MUSCLE_GROUP_LABELS: Record<MuscleGroup, string> = {
  pectorals: "Chest",
  back: "Back",
  deltoids: "Shoulders",
  biceps: "Biceps",
  triceps: "Triceps",
  forearms: "Forearms",
  abdominals: "Abs",
  obliques: "Obliques",
  quadriceps: "Quads",
  hamstrings: "Hamstrings",
  glutes: "Glutes",
  calves: "Calves",
  trapezius: "Traps",
  latissimus_dorsi: "Lats",
  adductors: "Adductors",
};

export interface ExerciseLoad {
  resistance_unit: "kg" | "lbs";
  sets: ExerciseSet[];
}

export type ExerciseInstance = {
  snapshot_id: string;
} & ExerciseLoad;

export type ExerciseReference = {
  exercise_id: string;
} & ExerciseLoad;

// Exercises
export interface Exercise {
  id: string;
  name: string;
  description?: string;
  primary_muscle_groups: MuscleGroupWeight[];
  secondary_muscle_groups: MuscleGroupWeight[];
  start_resistance: number;
  default_resistance_unit: "kg" | "lbs";
  efficiency_factor: number;
}

export interface CreateExerciseRequest {
  name: string;
  description?: string;
  primary_muscle_groups: MuscleGroupWeight[];
  secondary_muscle_groups: MuscleGroupWeight[];
  start_resistance: number;
  default_resistance_unit: "kg" | "lbs";
  efficiency_factor: number;
}

export interface PatchExerciseRequest {
  name: string;
  description?: string;
  primary_muscle_groups: MuscleGroupWeight[];
  secondary_muscle_groups: MuscleGroupWeight[];
  start_resistance: number;
  default_resistance_unit: "kg" | "lbs";
  efficiency_factor: number;
}

export interface ExercisesListResponse {
  exercises: Exercise[];
}

export interface ExerciseResponse {
  exercise: Exercise;
}

// Exercise snapshots
export type ExerciseSnapshot = Omit<
  Exercise,
  "id" | "description" | "default_resistance_unit"
> & { id?: string; snapshot_id?: string };

export interface ExerciseSet {
  repetitions: number;
  resistance: number;
}

// Workout
export interface Workout {
  id: string;
  name: string;
  exercises: ExerciseInstance[];
  executed_at: string;
  status: "active" | "completed";
  visibility: "private" | "friends";
  description?: string;
}

export type PatchExerciseReference = ExerciseLoad & {
  snapshot_id?: string;
  exercise_id?: string;
};

export interface CreateWorkoutRequest {
  name: string;
  exercises: PatchExerciseReference[];
  executed_at: string;
  status: "active" | "completed";
  visibility: "private" | "friends";
}

export interface PatchWorkoutRequest {
  id: string;
  name?: string;
  description?: string;
  exercises?: PatchExerciseReference[];
  executed_at?: string;
  status?: "active" | "completed";
  visibility?: "private" | "friends";
}

export type ExerciseDetails = ExerciseSnapshot & ExerciseLoad;

export interface WorkoutDetails {
  id: string;
  name: string;
  exercises: ExerciseDetails[];
  executed_at: string;
  status: "active" | "completed";
  visibility: "private" | "friends";
  description?: string;
}

export interface WorkoutsListResponse {
  workouts: WorkoutDetails[];
}

export interface PaginationInfo {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}
export interface PaginatedWorkoutsResponse {
  workouts: WorkoutDetails[];
  pagination: PaginationInfo;
}

export interface WorkoutResponse {
  workout: WorkoutDetails;
}

// Workout routines
export interface WorkoutRoutine {
  id: string;
  name: string;
  exercises: ExerciseReference[];
}

export interface CreateWorkoutRoutineRequest {
  name: string;
  exercises: ExerciseReference[];
}

export interface PatchWorkoutRoutineRequest {
  name?: string;
  exercises?: ExerciseReference[];
}

export interface WorkoutRoutinesListResponse {
  routines: WorkoutRoutine[];
}

export interface WorkoutRoutineResponse {
  routine: WorkoutRoutine[];
}

// Shared workout routines
export interface SharedWorkoutRoutine {
  id: string;
  sender_id: string;
  name: string;
  exercises: ExerciseReference[];
}

export interface ShareWorkoutRoutineRequest {
  friendId: string;
  routineId: string;
}

export interface PatchSharedWorkoutRoutineRequest {
  exercises: ExerciseReference[];
}

// Activity feed

export interface ActivityFeedItem {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  executed_at: string;
  exercises: ExerciseDetails[];
  liked_by: { user_id: string; name: string }[];
}

export interface PaginatedActivityFeedResponse {
  activity_feed: ActivityFeedItem[];
  pagination: PaginationInfo;
}

// Friends

export interface CreateFriendRequest {
  recipient_email: string;
}

export interface FriendRequest {
  id: string;
  sender_email: string;
  recipient_email: string;
}

export interface HealthCheckResponse {
  status: string;
  message: string;
}

export interface ErrorResponse {
  error: string;
}
